import { For, Show, type JSX } from "solid-js";
import "./clipboard-panel.css";
import {
  clipboardAllowsLocalToRemote,
  clipboardAllowsRemoteToLocal,
  type ClipboardEngine,
  type ClipboardEntry,
  type ClipboardMode,
} from "./clipboard-engine";

// ─────────────────────────────────────────────────────────────────────────────
// Menu clipboard partagé — réutilisable par les panneaux RDP et Vidéo.
//
// Composant contrôlé (comme `Drawer`) : le panneau parent gère l'état `open`
// et fournit le `ClipboardEngine`. Le menu rend :
//  - un bouton 📋 qui toggle l'ouverture ;
//  - un dropdown avec les actions (envoyer / récupérer / coller-en-frappe) ;
//  - la liste d'historique (preview + badge local/distant + format + timestamp) ;
//  - un sélecteur de mode (none / local→remote / remote→local / bidirectional).
//
// Les actions `onPushClick` / `onRequestClick` / `onPasteAsTyping` sont fournies
// par le panneau car le transport diffère (CLIPRDR vs WS). Le moteur gère
// l'historique et le mode.
// ─────────────────────────────────────────────────────────────────────────────

export interface ClipboardMenuProps {
  /** Moteur clipboard partagé (mode + historique). */
  engine: ClipboardEngine;
  /** `true` pour afficher le menu ouvert. */
  open: boolean;
  /** Bascule l'ouverture (le parent possède l'état). */
  onToggle: () => void;
  /** Pousse le clipboard local vers le distant (ex. pasteLocalIntoRemote). */
  onPushClick: () => void;
  /** Récupère le clipboard distant vers le local (ex. requestRemoteIntoLocal). */
  onRequestClick: () => void;
  /**
   * Colle en frappe clavier (Vidéo seulement : keyText). `undefined` → bouton masqué.
   * RDP n'en a pas besoin (Ctrl+V synthétisé par le moteur).
   */
  onPasteAsTyping?: () => void;
  /** Libellé court du distant, pour les boutons ("machine distante" / "Windows"). */
  remoteLabel?: string;
}

const MODE_LABELS: Record<ClipboardMode, string> = {
  none: "Désactivé",
  "local-to-remote": "Local → Distant",
  "remote-to-local": "Distant → Local",
  bidirectional: "Bidirectionnel",
};

function formatRelativeTime(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return "à l'instant";
  if (diff < 3_600_000) return `il y a ${Math.floor(diff / 60_000)} min`;
  return `il y a ${Math.floor(diff / 3_600_000)} h`;
}

function formatBadge(entry: ClipboardEntry): string {
  const parts: string[] = [entry.origin === "local" ? "Loc" : "Dist"];
  for (const f of entry.formats) {
    if (f === "text/plain") parts.push("TXT");
    else if (f === "text/html") parts.push("HTML");
    else if (f === "image/png") parts.push("IMG");
    else if (f === "files") parts.push(`📁${entry.fileRefs?.length ?? 1}`);
  }
  return parts.join(" · ");
}

export function ClipboardMenu(props: ClipboardMenuProps): JSX.Element {
  const remoteLabel = () => props.remoteLabel ?? "machine distante";

  return (
    <div class="cb-menu">
      <button
        class="btn btn--sm"
        classList={{ "btn--info": props.open }}
        onClick={() => props.onToggle()}
        title="Presse-papier : envoi, copie et historique"
        aria-expanded={props.open}
      >
        📋 Presse-papier
      </button>

      <Show when={props.open}>
        <div class="cb-menu__dropdown">
          {/* ── Actions ── */}
          <div class="cb-menu__actions">
            <Show when={props.onPasteAsTyping}>
              <button
                class="btn btn--sm cb-menu__action"
                onClick={() => props.onPasteAsTyping?.()}
                title="Coller le presse-papier local en frappe clavier"
              >
                ⌨️ Coller (frappe)
              </button>
            </Show>
            <button
              class="btn btn--sm cb-menu__action"
              onClick={() => props.onPushClick()}
              disabled={!clipboardAllowsLocalToRemote(props.engine.mode())}
              title={`Envoyer mon presse-papier vers la ${remoteLabel()}`}
            >
              ↗️ Envoyer
            </button>
            <button
              class="btn btn--sm cb-menu__action"
              onClick={() => props.onRequestClick()}
              disabled={!clipboardAllowsRemoteToLocal(props.engine.mode())}
              title={`Récupérer le presse-papier de la ${remoteLabel()}`}
            >
              ↙️ Récupérer
            </button>
          </div>

          {/* ── Sélecteur de mode ── */}
          <label class="cb-menu__mode">
            <span class="cb-menu__mode-label">Sync</span>
            <select
              class="cb-menu__mode-select"
              value={props.engine.mode()}
              onChange={(e) => props.engine.setMode(e.currentTarget.value as ClipboardMode)}
            >
              <option value="bidirectional">Bidirectionnel</option>
              <option value="local-to-remote">Local → Distant</option>
              <option value="remote-to-local">Distant → Local</option>
              <option value="none">Désactivé</option>
            </select>
          </label>
          <p class="cb-menu__mode-hint">Mode actuel : {MODE_LABELS[props.engine.mode()]}</p>

          {/* ── Historique ── */}
          <div class="cb-menu__history-header">
            <span class="cb-menu__history-title">Historique</span>
            <Show when={props.engine.history().length > 0}>
              <button
                class="cb-menu__history-clear"
                onClick={() => props.engine.clearHistory()}
                title="Vider l'historique"
              >
                Vider
              </button>
            </Show>
          </div>

          <Show
            when={props.engine.history().length > 0}
            fallback={<p class="cb-menu__history-empty">Aucun transfert pour le moment.</p>}
          >
            <ul class="cb-menu__history-list">
              <For each={props.engine.history()}>
                {(entry) => (
                  <li class="cb-menu__entry" classList={{ "cb-menu__entry--local": entry.origin === "local" }}>
                    <div class="cb-menu__entry-badge">{formatBadge(entry)}</div>
                    <div class="cb-menu__entry-preview">{entry.textPreview || "(contenu binaire)"}</div>
                    <div class="cb-menu__entry-time">{formatRelativeTime(entry.timestamp)}</div>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </div>
      </Show>
    </div>
  );
}
